import React, { useState } from 'react'
import i18n from 'simple-react-i18n'
import PropTypes from 'prop-types'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, Step, StepLabel, Stepper } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { StyledFieldSet } from 'components/StyledElements'
import Select from 'components/forms/Select'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import InstallationAction from 'installation/actions/InstallationAction'
import { nbPerPageLabelMedium } from 'referencial/constants/ReferencialConstants'
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import Spinner from 'components/progress/Spinner'
import { CardTable } from 'components/datatable/NewTable'

const STEP = {
    SETTINGS: 1,
    STATUS: 2,
    SUCCESS: 3,
}

const importTypeDone = [2, 9, 10, 11, 12]

const useStyles = makeStyles(() => ({
    label: {
        '&.MuiStepLabel-alternativeLabel': {
            marginTop: '3px !important',
        },
    },
}))

const ImportDescDialog = ({
    isOpen = false,
    setIsOpen = () => { },
}) => {
    const dispatch = useDispatch()
    const classes = useStyles()

    const {
        installationsTypes,
    } = useSelector(store => ({
        installationsTypes: store.InstallationReducer.installationsTypes,
    }), shallowEqual)

    const [step, setStep] = useState(STEP.SETTINGS)
    const [fileName, setFileName] = useState()
    const [fileContent, setFileContent] = useState()
    const [installationType, setInstallationType] = useState()
    const [dataloaded, setDataloaded] = useState(false)
    const [errors, setErrors] = useState([])

    const onUploadFile = (e) => {
        const reader = new FileReader()
        const file = e.target.files[0]
        if (file) {
            setFileName(file.name)
            reader.onloadend = (event) => {
                const split = event.target.result.split(',')
                setFileContent(split[1])
            }
            reader.readAsDataURL(file)
        }
    }

    return (
        <Dialog open={isOpen} maxWidth='lg'>
            <DialogTitle style={{ margin: '0', padding: '0' }}>
                <Grid container justifyContent='space-between' alignItems='center' style={{ padding: '10 20' }}>
                    {i18n.importLabel}
                    <CloseIcon
                        style={{ color: 'white' }}
                        sx={{ cursor: 'pointer', fontSize: 30 }}
                        onClick={() => setIsOpen(false)}
                    />
                </Grid>
                <Grid container justifyContent='center' style={{ backgroundColor: 'white', paddingTop: '5px' }}>
                    <Grid size={6}>
                        <Stepper
                            alternativeLabel
                            activeStep={step - 1}
                            style={{ zoom: '2' }}
                        >
                            <Step>
                                <StepLabel classes={classes}>{i18n.settings}</StepLabel>
                            </Step>
                            <Step>
                                <StepLabel classes={classes}>{i18n.status}</StepLabel>
                            </Step>
                            <Step>
                                <StepLabel classes={classes}>{i18n.success}</StepLabel>
                            </Step>
                        </Stepper>
                    </Grid>
                </Grid>
            </DialogTitle>
            <DialogContent>
                {
                    step === STEP.SETTINGS && (
                        <StyledFieldSet>
                            <Grid container style={{ padding: '10 10 0' }}>
                                <Grid size={12}>
                                    <Select
                                        value={installationType}
                                        label={i18n.installationType}
                                        options={installationsTypes.filter(it => importTypeDone.includes(it.code))}
                                        onChange={setInstallationType}
                                    />
                                </Grid>
                                <Grid size={7}>
                                    <div className='file-field input-field'>
                                        <div className='btn'>
                                            <span>{i18n.importLabel}</span>
                                            <input
                                                accept='.xls, .xlsx'
                                                type='file'
                                                onChange={onUploadFile}
                                            />
                                        </div>
                                        <div className='file-path-wrapper'>
                                            <input className='file-path validate' type='text' />
                                        </div>
                                    </div>
                                </Grid>
                            </Grid>
                        </StyledFieldSet>
                    )
                }
                {
                    step === STEP.STATUS && (dataloaded ? (
                        <Grid justifyContent='center' alignItems='center' paddingTop={2}>
                            <CardTable
                                title={i18n.errors}
                                rows={errors}
                                headers={['code', 'error']}
                                rowsPerPageOptions={ nbPerPageLabelMedium }
                            />
                        </Grid>
                    ) : (
                        <StyledFieldSet style={{ marginTop: '20px', padding: '20 0' }}>
                            <Stack spacing={2} justifyContent='center' alignItems='center'>
                                <h4>{i18n.jobProgressMessage}</h4>
                                <h5>{i18n.jobProgressDescription}</h5>
                                <Spinner />
                            </Stack>
                        </StyledFieldSet>
                    ))
                }
                {
                    step === STEP.SUCCESS && (
                        <StyledFieldSet style={{ marginTop: '20px', padding: '20 0' }}>
                            <Stack spacing={2} justifyContent='center' alignItems='center'>
                                <h4>{i18n.jobSuccessMessage}</h4>
                                <h5>{i18n.jobSuccessDescription}</h5>
                                <CheckCircleIcon style={{ color: 'green', fontSize: 40 }} />
                            </Stack>
                        </StyledFieldSet>
                    )
                }
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-evenly' }}>
                <Button
                    variant='contained'
                    disabled={step === STEP.SETTINGS}
                    onClick={() => {
                        setStep(STEP.SETTINGS)
                        setDataloaded(false)
                        setFileContent()
                        setFileName()
                        setErrors()
                    }}
                >
                    {i18n.previous}
                </Button>
                <Button
                    variant='contained'
                    disabled={!(step === STEP.SETTINGS && fileContent && installationType)}
                    onClick={() => {
                        setDataloaded(false)
                        setStep(STEP.STATUS)
                        dispatch(InstallationAction.importInstallationDescription({
                            fileName,
                            fileContent,
                            installationType,
                        })).then(data => {
                            if (data?.length) {
                                setDataloaded(true)
                                setErrors(data)
                            } else if (!data) {
                                setStep(STEP.SETTINGS)
                            } else {
                                setStep(STEP.SUCCESS)
                            }
                        })
                    }}
                >
                    {i18n.importLabel}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

ImportDescDialog.propTypes = {
    isOpen: PropTypes.bool,
    setIsOpen: PropTypes.func,
}

export default ImportDescDialog